import { useTranslation } from 'react-i18next'
import { MultiFiltro } from '@/components/common/MultiFiltro'
import {
  BarraFiltrosActivos, descripcionDe, filtrosDe, type FiltroActivo,
} from '@/components/common/BarraFiltrosActivos'
import { etiquetaCatalogoDe } from '@/utils/filtroArticulo'
import type { ProduccionDiariaSortKey } from '@/types'
import {
  filterFieldCls,
  filterLabelCls,
  filterCardCls,
} from '@/components/common/FilterBar'
import { DateRangePicker } from '@/components/common/DateRangePicker'
import { ItemTypeFilter } from '@/components/common/ItemTypeFilter'
import type { ItemType } from '@/utils/itemType'

interface Props {
  dark: boolean
  desde: string
  hasta: string
  q: string[]
  /** Catálogos de las filas cargadas. Artículo admite además texto libre;
   *  lote y OP son dominios cerrados y sólo aceptan lo que existe. */
  opcionesArticulo?: { valor: string; titulo: string; detalle?: string }[]
  opcionesLote?: { valor: string; titulo: string; detalle?: string }[]
  opcionesOp?: { valor: string; titulo: string; detalle?: string }[]
  lote: string[]
  numop: string[]
  empresa: string
  estado: string
  familia: string
  rendimiento: string
  tipo: Set<ItemType>
  sortKey: ProduccionDiariaSortKey
  sortDir: 'asc' | 'desc'
  /** Feedback de resultados, ya formateado por la página. */
  resumen?: string
  showEmpresaFilter: boolean
  allowedEmpresas: string[]
  allEstados: string[]
  allFamilias: string[]
  avp: boolean
  setDesde: (v: string) => void
  setHasta: (v: string) => void
  setQ: (v: string[]) => void
  setLote: (v: string[]) => void
  setNumop: (v: string[]) => void
  setEmpresa: (v: string) => void
  setEstado: (v: string) => void
  setFamilia: (v: string) => void
  setRendimiento: (v: string) => void
  setTipo: (v: Set<ItemType>) => void
  setSortKey: (v: ProduccionDiariaSortKey) => void
  setSortDir: (v: 'asc' | 'desc') => void
  clearAll: () => void
}

/** Fecha ISO a dd/mm/aaaa para el chip. Un guion cuando el extremo está abierto. */
function fechaCorta(iso: string): string {
  return iso ? iso.split('-').reverse().join('/') : '…'
}

export function ProduccionDiariaFilters({
  dark, desde, hasta, q, opcionesArticulo = [], opcionesLote = [], opcionesOp = [], lote, numop, empresa, estado, familia, rendimiento, tipo, avp,
  sortKey, sortDir, resumen, showEmpresaFilter, allowedEmpresas,
  allEstados, allFamilias,
  setDesde, setHasta, setQ, setLote, setNumop, setEmpresa,
  setEstado, setFamilia, setRendimiento, setTipo,
  setSortKey, setSortDir, clearAll,
}: Props) {
  const { t } = useTranslation()

  const SORT_OPTIONS: { key: ProduccionDiariaSortKey; label: string }[] = [
    { key: 'fecha',          label: t('production.sort.date') },
    { key: 'articulo',       label: t('production.sort.article') },
    { key: 'numOp',          label: t('production.sort.opNum') },
    { key: 'qtyProducida',   label: t('production.sort.qtyProduced') },
    { key: 'qtyPlanificada', label: t('production.sort.qtyPlanned') },
    { key: 'estadoOp',       label: t('production.sort.status') },
  ]

  const RENDIMIENTO_OPTIONS = [
    { value: 'high',      label: t('production.filter.perf90'),    color: 'text-emerald-500' },
    { value: 'mid',       label: t('production.filter.perf70'),    color: 'text-amber-500'  },
    { value: 'low',       label: t('production.filter.perfLow'),   color: 'text-red-500'    },
    { value: 'unplanned', label: t('production.filter.unplanned'), color: 'text-slate-400'  },
  ]

  const btnBase    = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  const filtrosActivos: FiltroActivo[] = [
    // El rango va como un chip más, y no sólo dentro de su control. Ahora que los
    // filtros sobreviven al cambio de pantalla, volver con un rango puesto y sin
    // rastro de él en la franja dejaba la lista corta sin explicación —y sin
    // «Limpiar todos», que sólo se pinta cuando hay algún chip.
    ...(desde || hasta
      ? [{
          clave: 'fechas', campo: t('common.filtros.fechas'),
          valor: [fechaCorta(desde), fechaCorta(hasta)].join(' – '),
          quitar: () => { setDesde(''); setHasta('') },
        }]
      : []),
    // Sólo el chip de ARTÍCULO lleva rótulo de catálogo (código formateado); los
    // de lote y OP van tal cual porque no son códigos de artículo.
    ...filtrosDe(q, setQ, {
      campo: t('production.col.article'),
      etiquetaDe: etiquetaCatalogoDe(opcionesArticulo),
      tooltipDe: descripcionDe(opcionesArticulo), prefijo: 'a:',
    }),
    ...filtrosDe(lote, setLote, {
      campo: t('production.filter.lot'),
      tooltipDe: descripcionDe(opcionesLote), prefijo: 'l:',
    }),
    ...filtrosDe(numop, setNumop, {
      campo: t('production.filter.opNumber'),
      tooltipDe: descripcionDe(opcionesOp), prefijo: 'o:',
    }),
    ...(empresa ? [{ clave: 'emp', campo: t('common.company'), valor: empresa.toUpperCase(), quitar: () => setEmpresa('') }] : []),
    ...(estado ? [{ clave: 'est', campo: t('common.status'), valor: t('production.status.' + estado, { defaultValue: estado }), quitar: () => setEstado('') }] : []),
    ...(familia ? [{ clave: 'fam', campo: t('common.family'), valor: familia, quitar: () => setFamilia('') }] : []),
  ]

  const labelCls   = filterLabelCls(dark)

  return (
    <div className={filterCardCls(dark)}>
      {/* ── Fila 1: fechas + texto + identificadores + empresa ─────────── */}
      <div className="flex flex-wrap gap-3 items-end p-3 pb-2.5">

        {/* Desde / Hasta */}
        <DateRangePicker
          dark={dark}
          desde={desde}
          hasta={hasta}
          onDesde={setDesde}
          onHasta={setHasta}
        />

        {/* Artículo / Descripción */}
        <div className="flex flex-col gap-1 min-w-[160px] flex-1">
          <label className={labelCls}>{t('production.filter.articleDesc')}</label>
          <MultiFiltro
            dark={dark}
            permitirLibre
            opciones={opcionesArticulo}
            seleccion={q}
            onSeleccion={setQ}
            placeholder={t('common.search')}
          />
        </div>

        {/* Lote */}
        <div className="flex flex-col gap-1 min-w-[100px]">
          <label className={labelCls}>{t('production.filter.lot')}</label>
          <MultiFiltro
            dark={dark}
            opciones={opcionesLote}
            seleccion={lote}
            onSeleccion={setLote}
            placeholder={t('production.filter.lotPlaceholder')}
          />
        </div>

        {/* Nº operación */}
        <div className="flex flex-col gap-1 min-w-[100px]">
          <label className={labelCls}>{t('production.filter.opNumber')}</label>
          <MultiFiltro
            dark={dark}
            opciones={opcionesOp}
            seleccion={numop}
            onSeleccion={setNumop}
            placeholder={t('production.filter.opPlaceholder')}
          />
        </div>

        {/* Empresa */}
        {showEmpresaFilter && (
          <div className="flex flex-col gap-1 min-w-[90px]">
            <label className={labelCls}>{t('common.company')}</label>
            <select
              value={empresa}
              onChange={e => setEmpresa(e.target.value)}
              className={`${filterFieldCls(dark, !!empresa)} cursor-pointer`}
            >
              <option value="">{t('common.allCompanies')}</option>
              {allowedEmpresas.map(e => (
                <option key={e} value={e}>{e.toUpperCase()}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Fila 2: filtros categóricos + orden ────────────────────────── */}
      <div className={`flex flex-wrap gap-3 items-end px-3 py-2.5 border-t ${dark ? 'border-white/6' : 'border-slate-100'}`}>

        {/* Estado */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className={labelCls}>{t('production.filter.opStatus')}</label>
          <select
            value={estado}
            onChange={e => setEstado(e.target.value)}
            className={`${filterFieldCls(dark, !!estado)} cursor-pointer`}
          >
            <option value="">{t('production.filter.allStatuses')}</option>
            {allEstados.map(e => (
              <option key={e} value={e}>{t('production.status.' + e, { defaultValue: e })}</option>
            ))}
          </select>
        </div>

        {/* Familia */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className={labelCls}>{t('common.family')}</label>
          <select
            value={familia}
            onChange={e => setFamilia(e.target.value)}
            className={`${filterFieldCls(dark, !!familia)} cursor-pointer`}
          >
            <option value="">{t('common.allFamilies')}</option>
            {allFamilias.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>

        {/* Tipo de artículo (prefijo Q/P/X) */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className={labelCls}>{t('common.itemType.label')}</label>
          <ItemTypeFilter dark={dark} value={tipo} onChange={setTipo} avp={avp} />
        </div>

        {/* Rendimiento */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className={labelCls}>{t('production.filter.performance')}</label>
          <select
            value={rendimiento}
            onChange={e => setRendimiento(e.target.value)}
            className={`${filterFieldCls(dark, !!rendimiento)} cursor-pointer`}
          >
            <option value="">{t('production.filter.allPerformance')}</option>
            {RENDIMIENTO_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Rendimiento active badge */}
        {rendimiento && (
          <div className="flex items-end pb-0.5">
            <span className={`text-[11px] font-semibold px-2 py-1 rounded ${
              rendimiento === 'high'      ? 'bg-emerald-500/15 text-emerald-500' :
              rendimiento === 'mid'       ? 'bg-amber-500/15 text-amber-500'    :
              rendimiento === 'low'       ? 'bg-red-500/15 text-red-500'        :
                                           `${dark ? 'bg-white/8 text-slate-400' : 'bg-slate-100 text-slate-500'}`
            }`}>
              {RENDIMIENTO_OPTIONS.find(o => o.value === rendimiento)?.label}
            </span>
          </div>
        )}

        <div className="flex-1" />

        {/* Ordenar */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className={labelCls}>{t('common.sortBy')}</label>
          <div className="flex gap-1">
            <select
              value={sortKey}
              onChange={e => setSortKey(e.target.value as ProduccionDiariaSortKey)}
              className={`${filterFieldCls(dark)} flex-1 cursor-pointer`}
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
              className={`${btnNeutral} !px-2 shrink-0 flex items-center justify-center`}
              title={sortDir === 'asc' ? t('common.asc') : t('common.desc')}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {sortDir === 'asc'
                  ? <path d="M3 9l4-4 4 4" />
                  : <path d="M3 5l4 4 4-4" />
                }
              </svg>
            </button>
          </div>
        </div>

      </div>

      {/* Franja común, separada de los controles: éstos no se mueven nunca.
          Aquí el prefijo del campo SÍ hace falta: conviven artículo, lote y OP,
          y sin él tres códigos parecidos serían indistinguibles. */}
      {filtrosActivos.length > 0 && (
        <div className="px-3 pb-3 -mt-1">
          <BarraFiltrosActivos
            dark={dark}
            filtros={filtrosActivos}
            onLimpiar={clearAll}
            resumen={resumen}
          />
        </div>
      )}
    </div>
  )
}
